import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import DocumentActivityListener from 'tio-employee/components/slr/document-activity-listener';
import { VStack } from 'tio-ui/components/layout';
import ElfTile from 'tio-employee/components/slr/elf-tile';
import ReimaginedContributionHistory from 'tio-employee/components/slr/contribution-history';
import LoanStandingAttentionCard from 'tio-employee/components/slr/loan-standing-attention-card';
import {
  shouldShowSlrLoanStandingAttentionCard,
  slrReceivingAccountLabel,
} from 'tio-employee/utils/slr-loan-standing-verification';
import {
  elfTileState,
  isLrapEnrolled,
  shouldShowElfTile,
} from 'tio-employee/utils/lrap-enrollment';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type LrapEnrollmentService from 'tio-employee/services/lrap-enrollment';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { AuthenticatedSlrDashboardModel } from 'tio-employee/routes/authenticated/slr/dashboard';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';
import type { SlrNameMatchConfirmation } from 'tio-data/requests/slr-loan-standing-verification';

interface SlrDashboardSignature {
  Args: {
    model: AuthenticatedSlrDashboardModel;
  };
}

// Coalesces reflection-broadcast bursts (a multi-document rerun emits one
// content_reflected per document) into a single refresh.
const REMOTE_ACTIVITY_DEBOUNCE_MS = 300;

// The good-standing verdict is evaluated asynchronously AFTER the reflection
// the activity event announces, so the first refresh can read a pre-verdict
// state (drawer/admin uploads never broadcast their verdict here — see
// document-activity-listener). One re-read after this settle window converges
// those flows without waiting for the participant's next visit.
const EVAL_SETTLE_RECHECK_MS = 8_000;

// Mid-flight phases of an upload started FROM this page: its own settle path
// runs the same refresh recipe, and a remote-triggered refresh racing it
// could repaint the card under the live upload feedback.
const BUSY_UPLOAD_PHASES = new Set(['submitting', 'processing', 'verifying']);

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service declare lrapEnrollment: LrapEnrollmentService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;

  // Post-upload refresh result, keyed to the model value it superseded. A
  // route refresh (e.g. switching the receiving account re-runs the model
  // hook) replaces the model value, which invalidates this override
  // automatically — a plain tracked copy seeded from args would keep showing
  // the OLD account's alert after the model refreshed underneath it.
  @tracked private refreshedLoanStanding?: {
    supersedes: SlrLoanStandingAuditState | null;
    state: SlrLoanStandingAuditState | null;
  };

  get loanStandingVerification() {
    const fromModel = this.args.model.loanStandingVerification;
    const refreshed = this.refreshedLoanStanding;
    return refreshed && refreshed.supersedes === fromModel ? refreshed.state : fromModel;
  }

  get reimaginedModel() {
    return this.args.model.reimagined;
  }

  /** Receiving-account label with a masked number; the route model follows account changes and distinguishes accounts at one servicer. */
  get loanStandingAccountName() {
    const account = this.args.model.person?.accountReceivingContributions;
    if (!account) return null;
    return slrReceivingAccountLabel(account.institutionName, account.accountNumber) || null;
  }

  get lrapEnrollmentStatus() {
    return this.lrapEnrollment.isAvailable ? this.args.model.lrapEnrollmentStatus : null;
  }

  get showElfTile() {
    return shouldShowElfTile(this.lrapEnrollment.isAvailable, this.lrapEnrollmentStatus);
  }

  get elfTileState() {
    return elfTileState(this.lrapEnrollmentStatus);
  }

  get isLrapEnrolled() {
    return isLrapEnrolled(this.lrapEnrollmentStatus);
  }

  /**
   * The plan's selected eligibility-condition names — drives which upload
   * guidelines the verification card lists (tio-admin →
   * plans/eligibility-conditions-editor is the source of truth).
   */
  get planEligibilityConditions() {
    return this.sessionContext.currentEmployee?.plan?.eligibilityConditions?.map((c) => c.name);
  }

  /**
   * A live upload from THIS session that hasn't been dismissed yet — its
   * file-anchored feedback lives on the attention card, so the card must
   * outlive verdicts that would otherwise swap the page's presentation.
   */
  get hasLiveUploadActivity() {
    return this.slrLoanStandingVerification.liveUpload.phase !== 'idle';
  }

  get showLoanStandingAlert() {
    const { phase, outcome } = this.slrLoanStandingVerification.liveUpload;
    return shouldShowSlrLoanStandingAttentionCard(this.loanStandingVerification, {
      isActive: phase !== 'idle',
      settledStatus: phase === 'settled' ? (outcome?.status ?? null) : null,
    });
  }

  /**
   * The state flipped to eligible during THIS session (post-upload refresh) —
   * show the success confirmation once. A page that loads already-eligible
   * stays quiet (no attention section), matching the design's verified state.
   */
  get justVerified() {
    const refreshed = this.refreshedLoanStanding;
    if (refreshed?.state?.status !== 'eligible') return false;
    // Same identity check as loanStandingVerification: the refresh only
    // counts while it supersedes the CURRENT route model. After an account
    // switch/route refresh replaces the model, a stale eligible refresh must
    // not resurrect the success section over a page that loaded verified.
    if (refreshed.supersedes !== this.args.model.loanStandingVerification) return false;
    const previous = refreshed.supersedes;
    return !!previous && previous.status !== 'eligible';
  }

  get showAttentionSection() {
    return this.showLoanStandingAlert || this.justVerified;
  }

  /**
   * Records the Review Name panel answer. The service PATCHes
   * name_match_confirmation and returns the settled post-eval state, applied
   * through the same supersedes-tracking as the upload refresh so
   * justVerified fires when an affirmation ('confirmed'/'responsible') flips
   * the state to eligible.
   */
  confirmLoanStandingName = async (confirmation: SlrNameMatchConfirmation) => {
    const id = this.loanStandingVerification?.employeePlanAccountId;
    if (!id) return;
    const supersedes = this.args.model.loanStandingVerification;
    const state = await this.slrLoanStandingVerification.confirmNameMatch(id, confirmation);
    this.refreshedLoanStanding = { supersedes, state };
  };

  /**
   * A document reflection landed for this person somewhere else — an admin
   * reflect/rerun/manual entry, or another session's upload. Reuse the
   * settled-upload refresh recipe (verdict reload first, then a full route
   * refresh) so the alerts, page state, and Loan Accounts section all catch
   * up without the participant touching anything. Skipped while an upload
   * from THIS page is mid-flight — its own settle path refreshes, and racing
   * it would repaint the card under the live feedback.
   */
  handleRemoteDocumentActivity = restartableTask(async () => {
    await timeout(REMOTE_ACTIVITY_DEBOUNCE_MS);
    if (BUSY_UPLOAD_PHASES.has(this.slrLoanStandingVerification.liveUpload.phase)) return;
    await this.refreshLoanStandingVerification.perform();
    // Second pass for the pre-verdict race (EVAL_SETTLE_RECHECK_MS above).
    // Restartable: fresh activity during the wait supersedes this pass.
    await timeout(EVAL_SETTLE_RECHECK_MS);
    if (BUSY_UPLOAD_PHASES.has(this.slrLoanStandingVerification.liveUpload.phase)) return;
    await this.refreshLoanStandingVerification.perform();
  });

  refreshLoanStandingVerification = dropTask(async () => {
    const supersedes = this.args.model.loanStandingVerification;
    // Post-write reader: the upload/evaluation never invalidates this query's
    // soft-cached document, so without reload a settled verdict can re-render
    // the pre-upload state. The verdict fetch comes FIRST — the route refresh
    // below re-runs a model hook whose Promise.all mostly fails closed, and a
    // rejection there must not leave the card stuck pre-verdict.
    const state = await this.slrLoanStandingVerification.fetchCurrentState({ reload: true });
    this.refreshedLoanStanding = { supersedes, state };
    // A settled verdict usually follows a reflection that added or updated
    // loan accounts — reload the route model so the Loan Accounts section
    // shows them without a manual page refresh (the model's person fetch is
    // already reload: true). Best-effort: the verdict is already applied
    // above, and a fresh model carries it anyway (which is also why the
    // supersedes identity check invalidating the override is fine).
    try {
      await this.router.refresh('authenticated.slr.dashboard');
    } catch (e) {
      console.warn('[SLR loan standing] Route refresh after verdict failed', e);
    }
  });

  <template>
    <VStack class="gap-8 min-w-0">
      <DocumentActivityListener @onActivity={{this.handleRemoteDocumentActivity.perform}} />
      <ReimaginedContributionHistory
        @model={{this.reimaginedModel}}
        @loanStandingState={{this.loanStandingVerification}}
        @slotAttentionCount={{if this.showAttentionSection 1 0}}
        @lrapEnrolled={{this.isLrapEnrolled}}
      >
        <:attention>
          {{! Card only — the unified "Needs Your Attention" section (one
              header covering this card and the server action items) is
              contribution-history's. }}
          {{#if this.loanStandingVerification}}
            {{#if this.showAttentionSection}}
              <LoanStandingAttentionCard
                @state={{this.loanStandingVerification}}
                @accountInstitutionName={{this.loanStandingAccountName}}
                @eligibilityConditions={{this.planEligibilityConditions}}
                @onRefresh={{this.refreshLoanStandingVerification.perform}}
                @onConfirmName={{this.confirmLoanStandingName}}
                data-test-slr-loan-standing-attention
              />
            {{/if}}
          {{/if}}
        </:attention>
        <:promo>
          {{#if this.showElfTile}}
            <ElfTile @state={{this.elfTileState}} />
          {{/if}}
        </:promo>
      </ReimaginedContributionHistory>
    </VStack>
  </template>
}
